import { Component, effect, inject, input, signal, computed } from '@angular/core';
import { KeyValuePair } from 'src/app/models/classes/key-value-pair.class';
import { IWidget } from 'src/app/models/interfaces/iwidget.interface';
import { CurrencyFormatPipe } from 'src/app/pipes/currency-format.pipe';
import { BURNT_ORANGE, GROSVENOR_BLUE, WHITE } from '@gcm/gcm-ui-angular';
import { HighchartsService } from 'src/app/services/highcharts-service.service';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { ActivityTrackingService } from 'src/app/services/activity-tracking.service';
import { BaseWidgetComponent } from 'src/app/shared/base-widget/base-widget.component';
import { GcmChartComponent } from 'src/app/shared/gcm-chart/gcm-chart.component';
import { InfoIconComponent } from 'src/app/shared/info-icon/info-icon.component';

type CommitmentValueWidgetKey =
  | 'commitmentsByStrategy'
  | 'remainingValueByStrategy'
  | 'commitmentsByInvestmentType'
  | 'remainingValueByInvestmentType'
  | 'commitmentsByVintageYear'
  | 'remainingValueByVintageYear';

export type CommitmentValueWidgetTabId = 'strategy' | 'investmentType' | 'vintageYear';

interface CommitmentValueWidgetTabConfig {
  id: CommitmentValueWidgetTabId;
  label: string;
  tooltip: string;
  tooltipDataCy: string;
  chartDataCy: string;
  commitmentKey: CommitmentValueWidgetKey;
  remainingValueKey: CommitmentValueWidgetKey;
  error: string;
  chartOptions: Highcharts.Options | null;
}

export type CommitmentValueWidgetTabOverrides = Partial<Record<CommitmentValueWidgetTabId, Pick<CommitmentValueWidgetTabConfig, 'label' | 'tooltip'>>>;

@Component({
  selector: 'app-commitment-value-widget',
  imports: [BaseWidgetComponent, MatTabsModule, GcmChartComponent, InfoIconComponent],
  templateUrl: './commitment-value-widget.component.html',
  styleUrls: ['./commitment-value-widget.component.scss']
})
export class CommitmentValueWidgetComponent implements IWidget {
  private highchartsService = inject(HighchartsService);
  private currencyFormat = inject(CurrencyFormatPipe);
  private activityTracking = inject(ActivityTrackingService);

  readonly widgets = input<Record<string, KeyValuePair<string | number, number>[]> | undefined>(undefined);
  readonly currency = input<string | undefined>(undefined);
  readonly displayTabs = input<CommitmentValueWidgetTabConfig['id'][] | undefined>(undefined);
  readonly tabOverrides = input<CommitmentValueWidgetTabOverrides | undefined>(undefined);
  error = '';
  chartStyle = { width: '99%', display: 'flex' };
  tabs = signal<CommitmentValueWidgetTabConfig[]>([
    {
      id: 'strategy',
      label: 'Strategy',
      tooltip: 'Deal level commitment & NAV by Strategy Type',
      tooltipDataCy: 'strategyTooltip',
      chartDataCy: 'strategyChart',
      commitmentKey: 'commitmentsByStrategy',
      remainingValueKey: 'remainingValueByStrategy',
      error: '',
      chartOptions: null
    },
    {
      id: 'investmentType',
      label: 'Investment Type',
      tooltip: 'Deal level commitment & NAV by Investment Type',
      tooltipDataCy: 'investmentTypeTooltip',
      chartDataCy: 'investmentTypeChart',
      commitmentKey: 'commitmentsByInvestmentType',
      remainingValueKey: 'remainingValueByInvestmentType',
      error: '',
      chartOptions: null
    },
    {
      id: 'vintageYear',
      label: 'Vintage Year',
      tooltip: 'Deal level commitment & NAV by Vintage Year',
      tooltipDataCy: 'vintageYearTooltip',
      chartDataCy: 'vintageYearChart',
      commitmentKey: 'commitmentsByVintageYear',
      remainingValueKey: 'remainingValueByVintageYear',
      error: '',
      chartOptions: null
    }
  ]);

  visibleTabs = computed(() => {
    const display = this.displayTabs();
    const overrides = this.tabOverrides();
    const tabs = display ? this.tabs().filter(tab => display.includes(tab.id)) : this.tabs();

    if (!overrides) return tabs;
    return tabs.map(tab => ({ ...tab, ...overrides[tab.id] }));
  });

  buildChartOptions(commitmentValueData: KeyValuePair<string | number, number>[], remainingValueData: KeyValuePair<string | number, number>[]) {
    const commitmentValueSum = commitmentValueData.reduce((sum, widget) => { return sum + widget.value }, 0);
    const remainingValueSum = remainingValueData.reduce((sum, widget) => { return sum + widget.value }, 0);
    const hasManyValues = commitmentValueData.length >= 8 || remainingValueData.length >= 8;

    return {
      chart: { height: 400, marginBottom: 75, marginRight: 0 },
      plotOptions: {
        series: {
          dataLabels: {
            formatter: function (this: Highcharts.Point) {
              return `${this.y}%`;
            },
            style: { fontSize: hasManyValues ? '10px' : '13px' }
          },
          states: {
            inactive: {
              opacity: 1
            }
          },
          keys: ['name', 'y', 'custom.value']
        },
        bar: {
          groupPadding: 0.125,
          pointWidth: hasManyValues ? 12 : 20
        }
      },
      legend: {
        enabled: true,
        align: 'right' as const,
        symbolRadius: 0,
        layout: 'vertical' as const,
        itemMarginBottom: 5,
        itemStyle: {
          cursor: 'default' as const,
          fontWeight: 'light',
          fontSize: '1em'
        },
        itemHoverStyle: {
          transition: 'none'
        },
        labelFormat: 'By {name}',
        events: {
          itemClick: () => false
        }
      },
      tooltip: {
        formatter: function (this: Highcharts.Point) {
          const data = `${this.series.name} <br/>` + this.options['custom']?.['value'] + ` <br/><hr class="mt-2 mb-2 mr-0 ml-0" style="background-color: ${WHITE}">`
          const footer = `<div>${this.key}</div>`
          return data + footer
        }
      },
      series: [{
        name: 'Commitment Value',
        color: GROSVENOR_BLUE,
        data: commitmentValueData.map(kvp => [kvp.key, Math.round((kvp.value / commitmentValueSum) * 1000) / 10, this.currencyFormat.transform(kvp.value, this.currency())])
      }, {
        name: 'Net Asset Value',
        color: BURNT_ORANGE,
        data: remainingValueData?.map(kvp => [kvp.key, Math.round((kvp.value / remainingValueSum) * 1000) / 10, this.currencyFormat.transform(kvp.value, this.currency())])
      }]
    }
  }

  private readonly rebuildTabs = effect(() => {
    const widgets = this.widgets();
    if (widgets) {
      this.tabs.update((tabs) => tabs.map((tab) => {
        const commitmentData = widgets[tab.commitmentKey] ?? [];
        const remainingValueData = widgets[tab.remainingValueKey] ?? [];
        const hasData = commitmentData.length > 0 || remainingValueData.length > 0;

        return {
          ...tab,
          error: hasData ? '' : this.highchartsService.noResultsText,
          chartOptions: this.buildChartOptions(commitmentData, remainingValueData)
        };
      }));
    }
  });

  handleChange(event: MatTabChangeEvent) {
    const tab = this.visibleTabs()[event.index];
    if (tab) {
      this.activityTracking.widgetTabChange('Strategy/investment type/vintage year widget', tab.label);
    }
  }
}
